import React, { ReactNode } from "react";
import styled, { css, DefaultTheme } from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

/** Below this width the zones stack. */
export const OVERVIEW_STACK_BREAKPOINT = 1000;
/** Below this width the triple layout puts every zone on its own row. */
const OVERVIEW_NARROW_BREAKPOINT = 900;
/** Height of the chart zones. */
export const OVERVIEW_ZONE_HEIGHT = 320;

interface OverviewPanelProps {
  /**
   * `split`: allocation | chart. `triple`: figures | chart | chart (pass the
   * zones as children).
   */
  variant?: "split" | "triple";
  allocationTitle?: ReactNode;
  /** A bare allocation donut (no border, no title). */
  allocation?: ReactNode;
  chart?: ReactNode;
  /** Height of the split variant's chart zone (defaults to the shared one). */
  zoneHeight?: number;
  children?: ReactNode;
  "data-testid"?: string;
}

/**
 * Top of the portfolio and trades pages, like the Dashboard's overview: one
 * panel split by hairlines into zones. Every zone carries its title inside,
 * on the same row, and the donut is centred in its zone.
 */
const OverviewPanel = ({
  variant = "split",
  allocationTitle,
  allocation,
  chart,
  zoneHeight,
  children,
  "data-testid": testId,
}: OverviewPanelProps) => (
  <Panel flush={true} data-testid={testId}>
    <Layout $variant={variant}>
      {children ?? (
        <>
          <OverviewZone>
            <OverviewZoneTitle>{allocationTitle}</OverviewZoneTitle>
            <AllocationBody>{allocation}</AllocationBody>
          </OverviewZone>
          <OverviewChartZone $height={zoneHeight}>{chart}</OverviewChartZone>
        </>
      )}
    </Layout>
  </Panel>
);

export default OverviewPanel;

const hairline = (p: { theme: DefaultTheme }) =>
  getNewLookTokens(p.theme).hairline;

const Layout = styled.div<{ $variant: "split" | "triple" }>`
  display: grid;
  ${(p) =>
    p.$variant === "split"
      ? css`
          grid-template-columns: 420px minmax(0, 1fr);
          > :first-child {
            border-right: 1px solid ${hairline};
          }
          @media (max-width: ${OVERVIEW_STACK_BREAKPOINT}px) {
            grid-template-columns: minmax(0, 1fr);
            > :first-child {
              border-right: 0;
              border-bottom: 1px solid ${hairline};
            }
          }
        `
      : css`
          grid-template-columns: 208px repeat(2, minmax(0, 1fr));
          > :not(:last-child) {
            border-right: 1px solid ${hairline};
          }
          @media (max-width: ${OVERVIEW_STACK_BREAKPOINT}px) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            > :first-child {
              grid-column: 1 / -1;
              border-right: 0;
              border-bottom: 1px solid ${hairline};
            }
          }
          @media (max-width: ${OVERVIEW_NARROW_BREAKPOINT}px) {
            grid-template-columns: minmax(0, 1fr);
            > :not(:last-child) {
              border-right: 0;
              border-bottom: 1px solid ${hairline};
            }
          }
        `}
`;
/** One zone of the overview; the panel draws the hairlines between them. */
export const OverviewZone = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 24px 24px 20px;
`;
/** A zone with a chart: fixed height, the chart fills what the title leaves. */
export const OverviewChartZone = styled(OverviewZone)<{ $height?: number }>`
  height: ${(p) => p.$height ?? OVERVIEW_ZONE_HEIGHT}px;
  min-height: ${(p) => p.$height ?? OVERVIEW_ZONE_HEIGHT}px;
  > :last-child {
    flex: 1;
    min-height: 0;
  }
`;
/** The header row height is shared with the chart zone's, so titles align. */
export const OverviewZoneTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
/** Centres the donut and legend in whatever height the chart gives the row. */
const AllocationBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 0;
  > * {
    flex: 1;
    min-width: 0;
  }
`;
